import React from "react";
import {
    DataTable,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableToolbar,
} from "@carbon/react";
import { useEffect, useMemo, useState } from "react";
import ToolBar from "../../Table/ToolBar/index.js";
import { renderCellValue } from "../../../utils/valueUtils.js";
import PanelWrapper from "../PanelWrapper";
import { useStore } from "react-redux";
import { ApiClient } from "../../../apiClient/apiClient.js";
import MediaPlaybackModal from "../../Modal/MediaPlaybackModal.js";
import { useTranslation } from "react-i18next";

const TablePanelContent = ({ chart, panelData }) => {
    const title = chart.title;
    const store = useStore();
    const { t } = useTranslation();

    const prepareTableData = (data) => {
        if (!data || data.length === 0) return { headers: [], rows: [] };

        const first = data[0] || {};
        const hasLegacyShape =
            first &&
            typeof first === "object" &&
            Array.isArray(first.headers) &&
            Array.isArray(first.rows);

        let rows = [];
        let headers = [];

        if (hasLegacyShape) {
            headers = first.headers;
            rows = first.rows;
        } else {
            rows = Array.isArray(data) ? data : [];
            const verboseMap = chart?.schema_verbose_map || {};
            const cols = rows.length ? Object.keys(rows[0]) : [];
            headers = cols.map((c) => ({
                key: c,
                header: c,
                verbose_name: verboseMap[c] || undefined,
            }));
        }

        return {
            headers,
            rows: rows.map((r, i) => ({
                id: r?.id ?? String(i),
                ...(r || {}),
            })),
        };
    };

    const colDescMap = useMemo(() => {
        const arr = Array.isArray(chart?.col_desc) ? chart.col_desc : [];
        return new Map(arr.map((d) => [d.column_name, d]));
    }, [chart]);

    const { headers, rows } = prepareTableData(panelData);

    const [rowState, setRowState] = useState(rows);

    const [mediaModalOpen, setMediaModalOpen] = useState(false);
    const [mediaPlaybackUrl, setMediaPlaybackUrl] = useState(null);
    const [mediaPlaybackType, setMediaPlaybackType] = useState(null);
    const [mediaPlaybackExpiresIn, setMediaPlaybackExpiresIn] = useState(null);
    const [mediaLoading, setMediaLoading] = useState(false);
    const [mediaError, setMediaError] = useState(null);
    const [lastMediaResource, setLastMediaResource] = useState(null);

    const closeMediaModal = () => {
        setMediaModalOpen(false);
        setMediaPlaybackUrl(null);
        setMediaPlaybackType(null);
        setMediaPlaybackExpiresIn(null);
        setMediaError(null);
        setMediaLoading(false);
        setLastMediaResource(null);
    };

    const requestPlaySession = async (resourceId) => {
        const response = await ApiClient.private(store).post(
            "/media/play-session/",
            {
                resource_id: resourceId,
                panel_id: chart.id,
            }
        );

        setMediaPlaybackUrl(response.data.playback_url);
        setMediaPlaybackType(response.data.type);
        setMediaPlaybackExpiresIn(response.data.expires_in);

        return response.data;
    };

    const openStorageResourcePlayback = async ({ value }) => {
        setLastMediaResource(value);
        setMediaModalOpen(true);
        setMediaPlaybackUrl(null);
        setMediaPlaybackType(null);
        setMediaPlaybackExpiresIn(null);
        setMediaError(null);
        setMediaLoading(true);

        try {
            await requestPlaySession(value);
        } catch (error) {
            console.error("No se pudo crear la sesión de reproducción", error);

            const status = error?.response?.status;

            if (status === 401) {
                setMediaError(t("media.sessionExpired"));
            } else if (status === 403) {
                setMediaError(t("media.playForbidden"));
            } else if (status === 404) {
                setMediaError(t("media.notFound"));
            } else {
                setMediaError(t("media.playUnexpected"));
            }
        } finally {
            setMediaLoading(false);
        }
    };

    const renewMediaPlaybackSession = async () => {
        if (!lastMediaResource) {
            throw new Error("No hay recurso multimedia activo para renovar.");
        }

        return requestPlaySession(lastMediaResource);
    };

    const downloadStorageResource = async ({ value }) => {
        try {
            const response = await ApiClient.private(store).post(
                "/media/download-session/",
                {
                    resource_id: value,
                    panel_id: chart.id,
                }
            );

            const downloadUrl = response.data.download_url;

            window.open(downloadUrl, "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error("No se pudo crear la sesión de descarga", error);

            const status = error?.response?.status;

            if (status === 401) {
                window.alert(t("media.sessionExpired"));
            } else if (status === 403) {
                window.alert(t("media.downloadForbidden"));
            } else if (status === 404) {
                window.alert(t("media.notFound"));
            } else {
                window.alert(t("media.downloadUnexpected"));
            }
        }
    };

    const retryMediaPlayback = () => {
        if (!lastMediaResource) return;

        openStorageResourcePlayback({
            value: lastMediaResource,
        });
    };

    const handlePlaybackError = (message) => {
        setMediaError(message);
        setMediaPlaybackUrl(null);
    };

    useEffect(() => {
        setRowState(rows);
    }, [panelData]);

    if (!rowState || !headers || !title) {
        return <div></div>;
    }

    return (
        <div className="table-panel">
            <DataTable rows={rowState} headers={headers} isSortable>
                {({ rows, headers, getHeaderProps, getTableProps }) => (
                    <TableContainer>
                        <div className="table-panel__top">
                            <div className="table-panel__title">
                                {title}
                            </div>

                            <div className="table-panel__toolbar">
                                <TableToolbar>
                                    <ToolBar
                                        data={rowState}
                                        notForTable={[]}
                                        setState={setRowState}
                                    />
                                </TableToolbar>
                            </div>
                        </div>

                        <Table {...getTableProps()}>
                            <TableHead>
                                <TableRow>
                                    {headers.map((header) => {
                                        const verbose =
                                            header?.verbose_name ??
                                            header?.verboseName ??
                                            header?.verbose;

                                        const colDesc = colDescMap.get(header.header);
                                        const alt = colDesc?.column_title || null;
                                        const label =
                                            alt && alt !== header.header
                                                ? alt
                                                : verbose ?? header.header;

                                        return (
                                            <TableHeader
                                                key={header.key || header.header}
                                                {...getHeaderProps({ header })}
                                            >
                                                {label}
                                            </TableHeader>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.cells.map((cell) => {
                                            const colName = cell?.info?.header;
                                            const colDesc = colDescMap.get(colName);

                                            return (
                                                <TableCell key={cell.id}>
                                                    {renderCellValue(cell.value, colDesc, {
                                                        onStorageResourcePlayClick:
                                                            openStorageResourcePlayback,
                                                        onStorageResourceDownloadClick:
                                                            downloadStorageResource,
                                                    })}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DataTable>

            <MediaPlaybackModal
                open={mediaModalOpen}
                onClose={closeMediaModal}
                onRetry={retryMediaPlayback}
                onPlaybackError={handlePlaybackError}
                onRenewPlaybackUrl={renewMediaPlaybackSession}
                playbackUrl={mediaPlaybackUrl}
                playbackType={mediaPlaybackType}
                playbackExpiresIn={mediaPlaybackExpiresIn}
                loading={mediaLoading}
                error={mediaError}
                title={title}
            />
        </div>
    );
};

const Index = ({ chart }) => {
    return (
        <PanelWrapper panel={chart} showDescription={true}>
            {(panelData) => (
                <TablePanelContent
                    chart={chart}
                    panelData={panelData}
                />
            )}
        </PanelWrapper>
    );
};

export default React.memo(Index);
